/**
 * Diagnóstico do discador: transforma o que o SDK do Callix e o navegador informam
 * (ou deixam de informar) em avisos claros para o agente, do jeito que o Callix web
 * faz. Cobre os casos em que o SDK "simplesmente não faz nada": sem microfone ou
 * saída de som, microfone bloqueado ou em uso, operador que não iniciou, sessão
 * derrubada, chamada manual que falhou, serviço offline.
 */
export type AudioIssueKind = 'unsupported' | 'insecure-context' | 'no-microphone' | 'mic-permission-denied' | 'mic-unavailable' | 'no-speaker' | 'enumerate-failed';

export interface DialerNotice {
  level: 'error' | 'warning';
  /** origem, para o chamador decidir o que fazer (ex.: esconder o teclado) */
  source: 'device' | 'startup' | 'session' | 'offline' | 'call';
  title: string;
  detail: string;
  /** o que o agente pode fazer */
  action?: string;
  /** código estável para testes e logs */
  code: string;
}

export interface AudioIssue extends DialerNotice {
  source: 'device';
  kind: AudioIssueKind;
}

const AUDIO_ISSUES: Record<AudioIssueKind, Omit<AudioIssue, 'kind' | 'source' | 'code'>> = {
  unsupported: { level: 'error', title: 'Navegador sem acesso ao microfone', detail: 'Este navegador não permite usar o microfone pela página.', action: 'Use Chrome, Edge ou Firefox atualizados.' },
  'insecure-context': { level: 'error', title: 'Página sem HTTPS', detail: 'O navegador só libera o microfone em páginas HTTPS (ou localhost).', action: 'Abra o discador pelo endereço com https://.' },
  'no-microphone': { level: 'error', title: 'Sem microfone', detail: 'Nenhum microfone foi encontrado neste computador, por isso o discador não consegue conectar nem ligar.', action: 'Conecte um fone ou headset e confira se o microfone está habilitado nas configurações de som do Windows; o discador reconhece sozinho quando aparecer.' },
  'mic-permission-denied': { level: 'error', title: 'Microfone bloqueado pelo navegador', detail: 'O navegador não permitiu que o discador use o microfone.', action: 'Clique no cadeado ao lado do endereço, permita o microfone e recarregue a página.' },
  'mic-unavailable': { level: 'error', title: 'Microfone em uso ou com falha', detail: 'Outro programa pode estar usando o microfone, ou o dispositivo falhou ao iniciar.', action: 'Feche outros aplicativos de áudio (Teams, Meet, gravadores) e tente de novo.' },
  'no-speaker': { level: 'warning', title: 'Sem saída de som', detail: 'Nenhum alto-falante ou fone foi encontrado: você não vai ouvir o cliente.', action: 'Conecte um fone e confira o dispositivo de saída nas configurações de som.' },
  'enumerate-failed': { level: 'warning', title: 'Não foi possível verificar os dispositivos de áudio', detail: 'O navegador não listou os dispositivos de áudio.', action: 'Confira as permissões da página e recarregue.' },
};

export function audioIssue(kind: AudioIssueKind, detailSuffix?: string): AudioIssue {
  const base = AUDIO_ISSUES[kind];
  return { ...base, detail: detailSuffix ? `${base.detail} (${detailSuffix})` : base.detail, kind, source: 'device', code: `device:${kind}` };
}

/** nomes de erro do getUserMedia por família (os nomes antigos do Chrome/Firefox incluídos) */
const NO_DEVICE_ERRORS = new Set(['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError']);
const DENIED_ERRORS = new Set(['NotAllowedError', 'PermissionDeniedError', 'PermissionDismissedError', 'SecurityError']);
const UNAVAILABLE_ERRORS = new Set(['NotReadableError', 'TrackStartError', 'AbortError', 'SourceUnavailableError']);

export function classifyGetUserMediaError(error: unknown): AudioIssueKind {
  const name = (error as { name?: string } | null)?.name ?? '';
  if (NO_DEVICE_ERRORS.has(name)) return 'no-microphone';
  if (DENIED_ERRORS.has(name)) return 'mic-permission-denied';
  if (UNAVAILABLE_ERRORS.has(name)) return 'mic-unavailable';
  return 'mic-unavailable';
}

export interface MediaDevicesLike {
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  enumerateDevices?: () => Promise<MediaDeviceInfo[]>;
}

export interface AudioCheckOptions {
  mediaDevices?: MediaDevicesLike | null;
  isSecureContext?: boolean;
  /** o navegador lista saídas de áudio (Chrome/Edge sim, Firefox não): só então "sem saída" é conclusivo */
  canListOutputs?: boolean;
}

/**
 * Verifica microfone e saída de som. Pede o microfone (o que também dispara o pedido de
 * permissão), solta a faixa logo em seguida e lista os dispositivos. Devolve o problema
 * mais grave ou null quando está tudo certo.
 */
export async function checkAudioDevices(options: AudioCheckOptions = {}): Promise<AudioIssue | null> {
  const mediaDevices = options.mediaDevices === undefined ? (typeof navigator !== 'undefined' ? navigator.mediaDevices : null) : options.mediaDevices;
  const secure = options.isSecureContext ?? (typeof window !== 'undefined' ? window.isSecureContext !== false : true);
  if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') return audioIssue(secure ? 'unsupported' : 'insecure-context');
  try {
    const stream = await mediaDevices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) track.stop();
  } catch (error) {
    const kind = classifyGetUserMediaError(error);
    const name = (error as { name?: string } | null)?.name;
    return audioIssue(kind, kind === 'mic-unavailable' && name ? name : undefined);
  }
  if (typeof mediaDevices.enumerateDevices !== 'function') return null;
  let devices: MediaDeviceInfo[];
  try {
    devices = await mediaDevices.enumerateDevices();
  } catch {
    return audioIssue('enumerate-failed');
  }
  if (!devices.some((device) => device.kind === 'audioinput')) return audioIssue('no-microphone');
  const canListOutputs = options.canListOutputs ?? (typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype);
  if (canListOutputs && !devices.some((device) => device.kind === 'audiooutput')) return audioIssue('no-speaker');
  return null;
}

// ------------------------------------------------------------------ SDK: partida, sessão, serviço

export type StartupErrorKind = 'unknownError' | 'missingAudioPermissions' | 'maxUsersReached' | 'userIsAlreadyConnectedOnAnotherDevice';

const STARTUP_NOTICES: Record<StartupErrorKind, Omit<DialerNotice, 'source' | 'code'>> = {
  missingAudioPermissions: { level: 'error', title: 'Discador sem acesso ao microfone', detail: 'O Callix não conseguiu usar o microfone para iniciar o atendimento.', action: 'Conecte ou habilite um microfone, permita o uso no navegador e recarregue a página.' },
  maxUsersReached: { level: 'error', title: 'Limite de usuários do Callix atingido', detail: 'O serviço já está com o número máximo de agentes conectados.', action: 'Aguarde um colega sair ou fale com o supervisor.' },
  userIsAlreadyConnectedOnAnotherDevice: { level: 'error', title: 'Usuário já conectado em outro lugar', detail: 'Este login está ativo em outro computador, aba ou no Callix web.', action: 'Saia de lá (ou feche a outra aba) e entre de novo aqui.' },
  unknownError: { level: 'error', title: 'O discador não conseguiu iniciar', detail: 'O Callix não respondeu como esperado ao iniciar o atendimento.', action: 'Recarregue a página; se continuar, confira a internet e avise o suporte.' },
};

export function describeStartupError(error?: string | null): DialerNotice {
  const kind: StartupErrorKind = error && error in STARTUP_NOTICES ? (error as StartupErrorKind) : 'unknownError';
  return { ...STARTUP_NOTICES[kind], source: 'startup', code: `startup:${kind}` };
}

export type SessionDropOriginator = 'breakTimeLimit' | 'idle' | 'manualCallTimeLimit' | 'supervisor' | 'user' | 'userBlocked' | 'exclusiveLogin';

const SESSION_DROP_NOTICES: Record<SessionDropOriginator, Omit<DialerNotice, 'source' | 'code'>> = {
  exclusiveLogin: { level: 'error', title: 'Você entrou em outro lugar', detail: 'Este login foi aberto em outro computador, aba ou no Callix web, e a sessão daqui foi encerrada.', action: 'Feche o outro acesso e entre de novo aqui.' },
  supervisor: { level: 'error', title: 'Sessão encerrada pelo supervisor', detail: 'Um supervisor desconectou você do atendimento.', action: 'Fale com o supervisor antes de entrar de novo.' },
  userBlocked: { level: 'error', title: 'Usuário bloqueado', detail: 'Seu usuário foi bloqueado no Callix.', action: 'Fale com o supervisor ou o administrador.' },
  breakTimeLimit: { level: 'warning', title: 'Tempo de pausa excedido', detail: 'A pausa passou do limite e o Callix encerrou a sessão.', action: 'Entre de novo para voltar ao atendimento.' },
  manualCallTimeLimit: { level: 'warning', title: 'Tempo de chamada manual excedido', detail: 'A chamada manual passou do limite configurado e a sessão foi encerrada.', action: 'Entre de novo para continuar.' },
  idle: { level: 'warning', title: 'Sessão encerrada por inatividade', detail: 'Ficou muito tempo sem atividade e o Callix desconectou.', action: 'Entre de novo para continuar.' },
  user: { level: 'warning', title: 'Você saiu do atendimento', detail: 'A sessão foi encerrada a seu pedido.', action: 'Entre de novo quando quiser voltar.' },
};

export function describeSessionDrop(originator?: string | null): DialerNotice {
  const kind = originator && originator in SESSION_DROP_NOTICES ? (originator as SessionDropOriginator) : null;
  if (!kind) return { level: 'error', source: 'session', code: 'session:unknown', title: 'Sessão encerrada', detail: `A sessão no Callix foi encerrada${originator ? ` (${originator})` : ''}.`, action: 'Entre de novo para continuar.' };
  return { ...SESSION_DROP_NOTICES[kind], source: 'session', code: `session:${kind}` };
}

export function describeOffline(): DialerNotice {
  return { level: 'error', source: 'offline', code: 'offline', title: 'Discador desconectado do Callix', detail: 'O atendimento está offline: sem internet, sem microfone ou o Callix derrubou a conexão.', action: 'Confira a internet e o microfone; o discador tenta reconectar sozinho. Se não voltar, recarregue a página.' };
}

// ------------------------------------------------------------------ SDK: falha de chamada

export interface CallFailureLike {
  cause?: string | null;
  rejectReason?: number | null;
  message?: string | null;
}

/** CallFailureRejectReason do SDK */
const REJECT_REASONS: Record<number, { title: string; detail: string; action?: string }> = {
  1: { title: 'Número inválido', detail: 'O Callix recusou o número discado.', action: 'Confira o DDD e o número.' },
  2: { title: 'Usuário não encontrado', detail: 'O Callix não reconheceu o usuário para esta chamada.', action: 'Saia e entre de novo no discador.' },
  3: { title: 'Equipe bloqueada para ligar', detail: 'Sua equipe está sem permissão de chamada manual neste momento.', action: 'Fale com o supervisor.' },
  4: { title: 'Número na black list', detail: 'Este número está na lista de bloqueio e não pode ser discado.' },
  5: { title: 'Sem rota para este número', detail: 'A telefonia não tem rota de saída para este destino.', action: 'Confira o número ou fale com o suporte.' },
  6: { title: 'Chamada recusada pelo Callix', detail: 'O Callix não autorizou esta chamada.' },
  7: { title: 'Erro interno do Callix', detail: 'O Callix falhou ao iniciar a chamada.', action: 'Tente de novo em instantes.' },
  8: { title: 'Ligação internacional não autorizada', detail: 'Chamadas internacionais estão desligadas para o seu usuário.', action: 'Fale com o supervisor.' },
  9: { title: 'Saldo de telefonia insuficiente', detail: 'A conta de telefonia do Callix está sem saldo.', action: 'Avise o administrador.' },
  10: { title: 'Caixa postal ou silêncio', detail: 'A chamada caiu em caixa postal ou ninguém falou do outro lado.' },
};

/** CallFailureCause do SDK */
const CAUSES: Record<string, { title: string; detail: string; action?: string }> = {
  Busy: { title: 'Número ocupado', detail: 'O destino está em outra ligação.', action: 'Tente de novo em alguns minutos.' },
  Unavailable: { title: 'Número indisponível', detail: 'O destino não atendeu ou está fora de área.' },
  'Not Found': { title: 'Número não existe', detail: 'A operadora respondeu que este número não existe.', action: 'Confira o DDD e o número.' },
  'Address Incomplete': { title: 'Número incompleto', detail: 'Faltam dígitos no número discado.', action: 'Confira o DDD e o número.' },
  'Blocked By Carrier': { title: 'Bloqueado pela operadora', detail: 'A operadora bloqueou a chamada para este destino.' },
  Rejected: { title: 'Chamada recusada', detail: 'O destino ou a operadora recusou a chamada.' },
  'SIP Failure Code': { title: 'Falha na telefonia', detail: 'A operadora devolveu um erro ao completar a chamada.', action: 'Tente de novo; se repetir, avise o suporte.' },
  'Bad Media Description': { title: 'Falha de áudio na chamada', detail: 'A negociação de áudio com a operadora falhou.', action: 'Recarregue a página e tente de novo.' },
  'Silence or Machine Detected': { title: 'Caixa postal ou silêncio', detail: 'A chamada caiu em caixa postal ou ninguém falou do outro lado.' },
};

export function describeCallFailure(failure?: CallFailureLike | null): DialerNotice {
  const reject = failure?.rejectReason != null ? REJECT_REASONS[failure.rejectReason] : undefined;
  const cause = failure?.cause ? CAUSES[failure.cause] : undefined;
  const chosen = reject ?? cause;
  if (chosen) return { level: 'warning', source: 'call', code: `call:${failure?.rejectReason != null ? `reject-${failure.rejectReason}` : failure?.cause}`, ...chosen };
  const raw = failure?.message || failure?.cause;
  return { level: 'warning', source: 'call', code: 'call:unknown', title: 'Chamada não concluída', detail: raw ? `O Callix informou: ${raw}.` : 'O Callix não informou o motivo.', action: 'Tente de novo; se repetir, avise o suporte.' };
}

// ------------------------------------------------------------------ prioridade

export interface NoticeInputs {
  deviceIssue?: AudioIssue | null;
  operatorState?: string | null;
  startupError?: string | null;
  sessionDrop?: string | null;
  callFailure?: CallFailureLike | null;
}

/** um aviso por vez, do mais grave para o menos grave */
export function pickNotice(inputs: NoticeInputs): DialerNotice | null {
  if (inputs.sessionDrop) return describeSessionDrop(inputs.sessionDrop);
  if (inputs.operatorState === 'startupError') {
    // o operador não iniciou por falta de microfone e sabemos o motivo exato: o aviso do dispositivo é mais útil que o genérico
    if (inputs.startupError === 'missingAudioPermissions' && inputs.deviceIssue) {
      const action = inputs.deviceIssue.action ?? '';
      return { ...inputs.deviceIssue, action: /recarregue a página/i.test(action) ? action : `${action} Depois, recarregue a página para o discador iniciar.`.trim() };
    }
    return describeStartupError(inputs.startupError);
  }
  if (inputs.deviceIssue) return inputs.deviceIssue;
  if (inputs.operatorState === 'manualCallFailed' || inputs.callFailure) return describeCallFailure(inputs.callFailure);
  if (inputs.operatorState === 'offline') return describeOffline();
  return null;
}
